"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Monitor, BookOpen, FileText, ArrowRight, PenTool } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const services = [{
  id: "item-1",
  title: "Web Copywriting",
  description: "Transform your digital presence with clear, conversion-focused messaging. We craft homepage narratives, landing page copy, and about pages that articulate your value proposition and resonate deeply with your target audience, turning casual visitors into loyal customers.",
  icon: Monitor,
  link: "#case-study-web-copy"
}, {
  id: "item-2",
  title: "Blog Strategy & Content",
  description: "Build authority and drive organic traffic with a strategic content roadmap. We develop editorial calendars and write high-quality, SEO-optimized articles that simplify complex industry topics, positioning your brand as a thought leader in the digital space.",
  icon: BookOpen,
  link: "#case-study-blog"
}, {
  id: "item-3",
  title: "Marketing Materials",
  description: "Equip your sales team with polished collateral that closes deals. From white papers and case studies to email sequences and brochures, we create cohesive marketing assets that maintain your brand voice and effectively communicate your competitive advantage.",
  icon: FileText,
  link: "#case-study-marketing"
}, {
  id: "item-4",
  title: "Content Strategy",
  description: "Move beyond ad-hoc posting with a comprehensive content framework. We analyze your market position and audience needs to develop a tailored strategy that aligns every piece of content with your overarching business goals for measurable growth.",
  icon: PenTool,
  link: "#case-study-strategy"
}];
export default function StrategicOfferings() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="strategic-offerings" ref={ref} className={cn("bg-background py-24 sm:py-32 transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      <div className="container mx-auto px-6 md:px-12 max-w-5xl">
        <div className="mb-16 md:mb-24 border-b border-border pb-8">
          <h2 className="text-4xl md:text-5xl font-serif font-medium text-primary tracking-tight mb-6">
            Strategic Offerings
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-serif max-w-2xl leading-relaxed">
            Expertly crafted content solutions designed to clarify your message
            and fuel business growth across digital platforms.
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full">
          {services.map((service, i) => <AccordionItem key={service.id} value={service.id} className="border-border mb-4 last:mb-0" data-index={i}>
              <AccordionTrigger className="hover:no-underline py-8 group">
                <div className="flex items-center gap-6 text-left">
                  <div className="flex items-center justify-center size-12 rounded-full bg-secondary/5 text-secondary group-hover:bg-secondary/10 transition-colors duration-300">
                    <service.icon className="size-6 stroke-[1.5px]" />
                  </div>
                  <span className="text-2xl md:text-3xl font-serif font-medium text-foreground group-hover:text-primary/80 transition-colors">
                    {service.title}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-8 pl-[4.5rem] pr-4 md:pr-12">
                <div className="space-y-6">
                  <p className="text-lg text-muted-foreground font-serif leading-relaxed max-w-3xl">
                    {service.description}
                  </p>
                  <div>
                    <Button asChild variant="link" className="p-0 h-auto text-primary font-medium text-base hover:text-primary/80 group/link">
                      <Link to={service.link}>
                        View Case Study
                        <ArrowRight className="ml-2 size-4 transition-transform group-hover/link:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>)}
        </Accordion>
      </div>
    </section>;
}